import { isNil } from 'lodash-es'
import { useMemo } from 'react'

import { usePrincipal } from '../../../../application/authentication'
import { useProposal } from '../../../../application/product/proposal/proposalContext'
import { Data, Proposal } from '../../../../domain'
import { AdjustmentInputConfig } from '../../../../state/configuration'
import { useApiErrorHandlingAsyncCallback } from '../../../auth'
import { hasChanges } from '../helpers'
import { toProposalAdjustment } from './adjustments'
import { toProposalData } from './data'

export const useProposalFormData = (inputs: AdjustmentInputConfig[]) => {
  const { proposal } = useProposal()

  return useMemo(() => {
    const toData = toProposalData(proposal)

    return inputs.reduce((data, input) => {
      if (isNil(input.adjustment)) throw new Error(`No adjustment for ${input.name}`)

      return {
        ...data,
        [input.name]: toData(input.adjustment),
      }
    }, {})
  }, [inputs, proposal])
}

interface ProposalAdjustmentProps {
  inputs: AdjustmentInputConfig[]
  applyRating: boolean
  onComplete?: (proposal?: Proposal) => Promise<void> | void
}

export const useProposalAdjustment = ({
  inputs,
  applyRating,
  onComplete,
}: ProposalAdjustmentProps) => {
  const existingData = useProposalFormData(inputs)

  const { apiClient } = usePrincipal()
  const { proposal, setProposal } = useProposal()

  return useApiErrorHandlingAsyncCallback(async (data: Data) => {
    const proposalAdjustment = toProposalAdjustment(
      inputs,
      applyRating,
      proposal,
    )(existingData, data)

    const updatedProposal = hasChanges(proposalAdjustment)
      ? await apiClient.updateProposal(proposal.proposalCode, proposalAdjustment)
      : proposal

    setProposal(updatedProposal)
    await onComplete?.(updatedProposal)
  })
}
